import { supabase } from "@/integrations/supabase/client";
import type { OpportunityWithRelations, OpportunityNote, OpportunityActivity, DealChampion } from "@/types/opportunity.types";

export const fetchOpportunities = async (options?: { limit?: number; offset?: number }) => {
  let query = supabase
    .from('opportunities')
    .select(`
      *,
      organization:organization_id (id, name),
      owner:owner_id (id, first_name, last_name),
      service:service_id (id, name)
    `)
    .order('expected_close_date');
    
  // Apply pagination if specified
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, (options.offset + (options.limit || 100)) - 1);
  }
  
  const { data, error } = await query;

  if (error) {
    console.error('Error fetching opportunities:', error);
    
    // If it's an auth error, return empty array instead of throwing
    if (error.code === 'PGRST116' || error.message.includes('JWT')) {
      console.warn('Authentication required to access opportunities');
      return [];
    }
    
    throw error;
  }
  
  console.log('Opportunities fetched successfully:', data?.length || 0);
  
  // Fetch contacts separately to avoid the relationship error
  const contactIds = data.map(opp => opp.contact_id).filter(Boolean);
  let contactMap = {};
  
  if (contactIds.length > 0) {
    const { data: contacts, error: contactError } = await supabase
      .from('contacts')
      .select('id, first_name, last_name')
      .in('id', contactIds);
      
    if (!contactError && contacts) {
      contactMap = contacts.reduce((acc, contact) => {
        acc[contact.id] = contact;
        return acc;
      }, {});
    }
  }
  
  // Process the data to match OpportunityWithRelations type
  const processedData = data.map(opp => {
    // Format deal_champion as object if it's a string ID
    const dealChampion = opp.deal_champion;
    
    // Add contact data to opportunities
    const contact = opp.contact_id && contactMap[opp.contact_id] 
      ? contactMap[opp.contact_id] 
      : undefined;
      
    return {
      ...opp,
      deal_champion: dealChampion,
      contact
    };
  });
  
  return processedData;
};

export const fetchOpportunitiesCount = async () => {
  const { count, error } = await supabase
    .from('opportunities')
    .select('*', { count: 'exact', head: true });
    
  if (error) throw error;
  return count || 0;
};

export const fetchOpportunityById = async (id: string) => {
  const { data, error } = await supabase
    .from('opportunities')
    .select(`
      *,
      organization:organization_id (id, name),
      owner:owner_id (id, first_name, last_name, email),
      service:service_id (id, name, description),
      parent:parent_opportunity_id (id, name)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  
  // Fetch contact separately
  if (data.contact_id) {
    const { data: contact, error: contactError } = await supabase
      .from('contacts')
      .select('id, first_name, last_name, email, job_title')
      .eq('id', data.contact_id)
      .single();
      
    if (!contactError && contact) {
      return {
        ...data,
        contact,
        deal_champion: data.deal_champion
      };
    }
  }
  
  return {
    ...data,
    contact: data.contact_id ? { id: data.contact_id } : undefined,
    deal_champion: data.deal_champion
  };
};

export const createOpportunity = async (opportunityData: any) => {
  const { data, error } = await supabase
    .from('opportunities')
    .insert(opportunityData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateOpportunity = async (id: string, opportunityData: any) => {
  const { data, error } = await supabase
    .from('opportunities')
    .update(opportunityData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const fetchOpportunityNotes = async (opportunityId: string) => {
  const { data, error } = await supabase
    .from('opportunity_notes')
    .select(`
      *,
      user:user_id (id, first_name, last_name, avatar_url)
    `)
    .eq('opportunity_id', opportunityId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  
  // Process the data to handle potential error objects in relations
  const processedData = data.map(note => {
    // First check if user exists and is an object
    let userValue = null;
    
    if (note.user && typeof note.user === 'object') {
      // Make sure it's not null and not an error object
      // Use optional chaining and type assertion to avoid null errors
      if (note.user && !('error' in (note.user || {}))) {
        userValue = note.user;
      }
    }
      
    return {
      ...note,
      user: userValue
    };
  });
  
  // Safely cast the processed data
  return processedData as unknown as OpportunityNote[];
};

export const createOpportunityNote = async (noteData: { opportunity_id: string; content: string; user_id?: string }) => {
  const { data, error } = await supabase
    .from('opportunity_notes')
    .insert(noteData)
    .select()
    .single();

  if (error) throw error;
  return data as OpportunityNote;
};

export const fetchOpportunityActivities = async (opportunityId: string) => {
  const { data, error } = await supabase
    .from('opportunity_activities')
    .select(`
      *,
      performer:performed_by (id, first_name, last_name, avatar_url)
    `)
    .eq('opportunity_id', opportunityId)
    .order('performed_at', { ascending: false });

  if (error) throw error;
  
  // Process the data to handle potential error objects in relations
  const processedData = data.map(activity => {
    // First check if performer exists and is an object
    let performerValue = null;
    
    if (activity.performer && typeof activity.performer === 'object') {
      // Make sure it's not null and not an error object
      // Use optional chaining and type assertion to avoid null errors
      if (activity.performer && !('error' in (activity.performer || {}))) {
        performerValue = activity.performer;
      }
    }
      
    return {
      ...activity,
      performer: performerValue
    };
  });
  
  // Safely cast the processed data
  return processedData as unknown as OpportunityActivity[];
};

export const logOpportunityActivity = async (activityData: {
  opportunity_id: string;
  activity_type: string;
  description: string;
  performed_by?: string;
}) => {
  const { data, error } = await supabase
    .from('opportunity_activities')
    .insert(activityData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteOpportunity = async (id: string) => {
  const { error } = await supabase
    .from('opportunities')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return true;
};

export const getFilteredOpportunities = async (filters: any) => {
  let query = supabase
    .from('opportunities')
    .select(`
      *,
      organization:organization_id (id, name),
      owner:owner_id (id, first_name, last_name)
    `);

  // Apply filters
  if (filters.stage) {
    query = query.eq('stage', filters.stage);
  }
  
  if (filters.minValue) {
    query = query.gte('value', filters.minValue);
  }
  
  if (filters.maxValue) {
    query = query.lte('value', filters.maxValue);
  }
  
  if (filters.startDate) {
    query = query.gte('expected_close_date', filters.startDate);
  }
  
  if (filters.endDate) {
    query = query.lte('expected_close_date', filters.endDate);
  }
  
  if (filters.orgId) {
    query = query.eq('organization_id', filters.orgId);
  }
  
  if (filters.ownerId) {
    query = query.eq('owner_id', filters.ownerId);
  }
  
  // Execute the query
  const { data, error } = await query;
  
  if (error) throw error;
  
  // Handle search filter in memory and process the data
  let result = data;
  if (filters.search) {
    const search = filters.search.toLowerCase();
    result = result.filter(
      opp => opp.name?.toLowerCase().includes(search) || 
             opp.organization?.name?.toLowerCase().includes(search)
    );
  }
  
  // Fetch contacts separately to avoid the relationship error
  const contactIds = result.map(opp => opp.contact_id).filter(Boolean);
  let contactMap = {};
  
  if (contactIds.length > 0) {
    const { data: contacts, error: contactError } = await supabase
      .from('contacts')
      .select('id, first_name, last_name')
      .in('id', contactIds);
      
    if (!contactError && contacts) {
      contactMap = contacts.reduce((acc, contact) => {
        acc[contact.id] = contact;
        return acc;
      }, {});
    }
  }
  
  // Process the data to match OpportunityWithRelations type
  const processedData = result.map(opp => {
    // Format deal_champion as object if it's a string ID
    const dealChampion = opp.deal_champion;
    
    // Add contact data to opportunities
    const contact = opp.contact_id && contactMap[opp.contact_id] 
      ? contactMap[opp.contact_id] 
      : undefined;
      
    return {
      ...opp,
      deal_champion: dealChampion,
      contact
    };
  });
  
  return processedData;
};
